/**
 * Which of a row's foldable actions have to leave the row so that what is
 * left fits on one line (AB#21303).
 *
 * The order detail's action row is one line, always. Two things on it never
 * fold — DQC and Order Communication on the left, the section icons and ⋯
 * on the right — and everything between them folds into the ⋯ menu from
 * the right as space runs out, last in first out, and comes back in the
 * same order as space returns. This is the arithmetic; the component does
 * the measuring.
 *
 * Widths are whatever the caller measured, in the same unit; `gap` is the
 * row's flex gap, which sits between every pair of visible things.
 */

export interface Foldable {
  key: string;
  width: number;
}

/**
 * @param fixedWidth   the width of everything that never folds, gaps between
 *                     those things included
 * @param fixedCount   how many never-folding things there are (for the gaps
 *                     they need to the foldables)
 * @returns the keys that must fold, in row order
 */
export const foldToFit = (
  foldables: Foldable[],
  rowWidth: number,
  fixedWidth: number,
  fixedCount: number,
  gap: number
): string[] => {
  if (foldables.length === 0) return [];

  // Everything visible: fixed things + n foldables, with a gap between each
  // pair, i.e. (fixedCount + n - 1) gaps in all. fixedWidth already carries
  // the gaps among the fixed things (fixedCount - 1 of them).
  let visible = foldables.length;
  const widthOf = (n: number): number =>
    fixedWidth + foldables.slice(0, n).reduce((sum, f) => sum + f.width, 0) + n * gap;

  while (visible > 0 && widthOf(visible) > rowWidth) {
    visible -= 1;
  }

  return foldables.slice(visible).map((f) => f.key);
};
